/**
 * EventsListBlock
 * Listing of name / date / location. Not a calendar.
 * Props-injected: fetchEvents({ source, limit, range }) => Event[]
 * blockType 'market-events' restricts to market days (server-side).
 */
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Section, Container, BlockTitle, BlockSubtitle, BlockHeader, t } from '../styles/shared';
import { resolveMediaServeUrl } from '../utils/mediaServeUrls';

const Banner = styled.div`
  background: ${t.primary};
  color: ${({ $textColor }) => $textColor || '#ffffff'};
  border-radius: 4px;
  text-align: center;
  padding: 28px 24px;
  margin: 0 0 36px;
  white-space: pre-line;
  line-height: 1.6;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: ${props => (props.$withImage ? '96px 130px 1fr' : '130px 1fr')};
  gap: 18px;
  align-items: ${props => (props.$withImage ? 'center' : 'baseline')};
  padding: 14px 0;
  border-bottom: 1px solid ${t.border};
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 6px;
  }
`;

const Thumb = styled.div`
  width: 96px; height: 72px; border-radius: 4px; overflow: hidden;
  background: ${t.bgAlt};
  img { width: 100%; height: 100%; object-fit: cover; display: block; }
  @media (max-width: 600px) { width: 100%; height: 160px; }
`;

const DateCol = styled.div`
  font-family: ${t.fontBody};
  font-weight: 700;
  color: ${t.primary};
  font-size: 1.05rem;
`;

const Title = styled.p`
  font-weight: 700;
  color: ${t.text};
  margin: 0 0 2px;
`;

const Sub = styled.p`
  font-size: 0.9rem;
  color: ${t.textLight};
  margin: 0;
`;

const Empty = styled.div`
  text-align: center;
  padding: 32px 16px;
  color: ${t.textLight};
  border: 2px dashed ${t.border};
  border-radius: 8px;
`;

const Loading = styled.div`
  text-align: center;
  padding: 48px;
  color: ${t.textLight};
`;

const fmtDate = (dateStr) => {
  if (!dateStr) return '';
  const plain = String(dateStr).split('T')[0];
  const [year, month, day] = plain.split('-').map(Number);
  if (!year || !month || !day) return '';
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  });
};

const fmtTime = (timeStr) => {
  if (!timeStr) return '';
  const [hours, minutes] = String(timeStr).split(':');
  const d = new Date();
  d.setHours(parseInt(hours, 10), parseInt(minutes, 10) || 0);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
};

const locationLine = (ev) => {
  const hours = [fmtTime(ev.start_time), fmtTime(ev.end_time)].filter(Boolean).join(' – ');
  const loc = [ev.location_name, ev.city, ev.state].filter(Boolean).join(', ');
  return [hours, loc].filter(Boolean).join(' · ');
};

const EventsListBlock = ({
  content = {},
  settings = {},
  fetchEvents,
  blockType,
  tenantSlug,
  apiBase,
}) => {
  const {
    title = '',
    subtitle = '',
    displayCount = 20,
    range = 'upcoming',
    showImage = false,
  } = content;
  const { backgroundColor = '', padding = 'large', textColor = '' } = settings;
  const source = blockType === 'market-events' ? 'market-events' : 'events';

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!fetchEvents) { setLoading(false); return; }
    fetchEvents({ source, limit: displayCount, range })
      .then((data) => setEvents(data || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [displayCount, range, source, fetchEvents]);

  const emptyCopy = source === 'market-events'
    ? 'No upcoming market days.'
    : 'No upcoming events.';

  return (
    <Section $padding={padding} $bgColor={backgroundColor} $textColor={textColor}>
      <Container>
        {(title || subtitle) && (
          <BlockHeader $center>
            {title && <BlockTitle style={{ textAlign: 'center', color: t.primary }}>{title}</BlockTitle>}
            {subtitle && !title && (
              <BlockSubtitle style={{ textAlign: 'center', ...(textColor ? { color: textColor } : {}) }}>
                {subtitle}
              </BlockSubtitle>
            )}
          </BlockHeader>
        )}
        {subtitle && title && <Banner $textColor={textColor}>{subtitle}</Banner>}

        {loading ? (
          <Loading>Loading events…</Loading>
        ) : !events.length ? (
          <Empty>{emptyCopy}</Empty>
        ) : (
          events.map((ev) => (
            <Row key={ev.id} $withImage={!!(showImage && ev.featured_image)}>
              {showImage && ev.featured_image && (
                <Thumb>
                  <img
                    src={resolveMediaServeUrl(ev.featured_image, tenantSlug, apiBase)}
                    alt=""
                    loading="lazy"
                  />
                </Thumb>
              )}
              <DateCol>{fmtDate(ev.event_date)}</DateCol>
              <div>
                <Title>{ev.title}</Title>
                <Sub>{locationLine(ev)}</Sub>
              </div>
            </Row>
          ))
        )}
      </Container>
    </Section>
  );
};

export default EventsListBlock;
